import {
    BrowserClient,
    Scope,
    defaultStackParser,
    httpContextIntegration,
    makeFetchTransport,
} from '@sentry/browser';

// Client isolé plutôt que Sentry.init : le widget embarque de vieux scripts jQuery
// et tourne en iframe chez des tiers. Sans intégration globale (window.onerror,
// console, fetch…), seul ce qui est capturé explicitement part vers Sentry.
// httpContextIntegration n'installe aucun hook : elle joint URL, user-agent et referrer.

// La query string porte `ypTicketingSession`, qui ouvre l'accès à la commande avec sa
// référence : elle ne quitte jamais le navigateur. La référence, seule, reste dans le chemin.
function stripQueryString(url) {
    try {
        const parsed = new URL(url);
        return `${parsed.origin}${parsed.pathname}`;
    } catch (e) {
        return undefined;
    }
}

function originOf(url) {
    try {
        return new URL(url).origin;
    } catch (e) {
        return undefined;
    }
}

function sanitizeEvent(event) {
    if (event.request) {
        const headers = event.request.headers || {};
        // eslint-disable-next-line no-param-reassign
        event.request = {
            url: event.request.url ? stripQueryString(event.request.url) : undefined,
            headers: {
                'User-Agent': headers['User-Agent'],
                Referer: headers.Referer ? originOf(headers.Referer) : undefined,
            },
        };
    }

    // Un script évalué par script-loader a pour fichier l'URL de la page, query string comprise
    ((event.exception && event.exception.values) || []).forEach((exception) => {
        ((exception.stacktrace && exception.stacktrace.frames) || []).forEach((frame) => {
            if (frame.filename) {
                // eslint-disable-next-line no-param-reassign
                frame.filename = stripQueryString(frame.filename) ?? frame.filename;
            }
            if (frame.abs_path) {
                // eslint-disable-next-line no-param-reassign
                frame.abs_path = stripQueryString(frame.abs_path) ?? frame.abs_path;
            }
        });
    });

    return event;
}

function createScope(dsn, environment) {
    const client = new BrowserClient({
        dsn,
        environment,
        transport: makeFetchTransport,
        stackParser: defaultStackParser,
        integrations: [httpContextIntegration()],
        sendDefaultPii: false,
        beforeSend: sanitizeEvent,
    });

    const scope = new Scope();
    scope.setClient(client);
    scope.setTag('feature', 'payplug');
    client.init();

    return scope;
}

export function createPaymentErrorReporter({ dsn, environment }) {
    const scope = dsn ? createScope(dsn, environment) : null;

    return {
        capture(error, { step, level = 'error', ...context } = {}) {
            const errorObj = error instanceof Error ? error : new Error(error);

            // eslint-disable-next-line no-console
            console.error('[PAYPLUG]', errorObj.message, { step, ...context });

            if (!scope) {
                return;
            }

            const eventScope = scope.clone();
            eventScope.setTag('step', step ?? 'unknown');
            if (context.name) {
                eventScope.setTag('error_name', context.name);
            }
            // Pile peu fiable (script évalué par script-loader) : une issue par étape
            eventScope.setFingerprint(['payplug', step ?? 'unknown']);
            eventScope.setLevel(level);
            eventScope.setContext('payplug', context);
            eventScope.captureException(errorObj);
        },
        // Identifiants de debug sans valeur d'accès : référence de paiement, id Payplug
        tag(key, value) {
            if (!scope || !value) {
                return;
            }

            scope.setTag(key, value);
        },
        breadcrumb(message, data = {}) {
            if (!scope) {
                return;
            }

            scope.addBreadcrumb({
                category: 'payplug',
                message,
                data,
                timestamp: Date.now() / 1000,
            });
        },
    };
}

window.YP = window.YP ?? {};
window.YP.paymentErrorReporter = createPaymentErrorReporter({
    dsn: YP_CONFIG.sentry.dsn,
    environment: env.NODE_ENV,
});
